import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import type { PDP } from '@/features/hrm/development/api';
import { PDP_STATUSES, pdpStatusLabel, pdpStatusVariant } from '@/features/hrm/development/constants';
import { PDP_BASE } from '../api';
import { CabinetEmptyState, CabinetListFooter, CabinetListToolbar } from '../components/listChrome';
import { createCabinetViewStore, viewModes } from '../store/cabinetView';
import { useCabinetPdps } from '../queries';

const usePdpUi = createCabinetViewStore('cabinet.pdp:ui', 'table', 1);
const MODES = viewModes(['table', 'list', 'cards', 'grouped']);
type PdpColumn = 'title' | 'period' | 'status';

const goalStatusLabel = (s: string): string =>
  ({ open: 'Open', in_progress: 'In progress', done: 'Done' })[s] ?? s;

export function CabinetPdpPage() {
  const navigate = useNavigate();
  const open = (p: PDP) => navigate(`/me/pdp/${p.public_id}`);
  const view = usePdpUi((s) => s.view);
  const setView = usePdpUi((s) => s.setView);
  const ordering = usePdpUi((s) => s.ordering);
  const setOrdering = usePdpUi((s) => s.setOrdering);

  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [page, setPage] = useState(1);

  const { data, isPending, isError, error, isFetching } = useCabinetPdps({
    page,
    search: search || undefined,
    ordering,
  });
  const rows = data?.results ?? [];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight">Development plan</h1>

      <CabinetListToolbar
        view={view}
        onViewChange={setView}
        modes={MODES}
        search={{
          value: searchInput,
          onChange: (v) => {
            setPage(1);
            setSearchInput(v);
          },
          placeholder: 'Search title or summary…',
        }}
        exportBase={PDP_BASE}
        exportParams={{ search: search || undefined, ordering }}
      />

      {isError ? (
        <p className="text-destructive text-sm">{(error as { message?: string })?.message}</p>
      ) : isPending ? (
        <Skeleton className="h-48 w-full" />
      ) : view === 'table' ? (
        <PdpTable rows={rows} ordering={ordering} onOrderingChange={setOrdering} onOpen={open} />
      ) : view === 'list' ? (
        <PdpList rows={rows} onOpen={open} />
      ) : view === 'cards' ? (
        <PdpCards rows={rows} onOpen={open} />
      ) : (
        <PdpGroupedByStatus rows={rows} onOpen={open} />
      )}

      <CabinetListFooter
        count={data?.count ?? 0}
        page={page}
        onPageChange={setPage}
        hasPrev={Boolean(data?.previous)}
        hasNext={Boolean(data?.next)}
        isFetching={isFetching}
        noun={['plan', 'plans']}
      />
    </div>
  );
}

function PdpTable({
  rows,
  ordering,
  onOrderingChange,
  onOpen,
}: {
  rows: PDP[];
  ordering: string | undefined;
  onOrderingChange: (next: string | undefined) => void;
  onOpen: (p: PDP) => void;
}) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableTableHead<PdpColumn> column="title" value={ordering} onChange={onOrderingChange}>
              Title
            </SortableTableHead>
            <SortableTableHead<PdpColumn> column="period" value={ordering} onChange={onOrderingChange}>
              Period
            </SortableTableHead>
            <SortableTableHead<PdpColumn> column="status" value={ordering} onChange={onOrderingChange}>
              Status
            </SortableTableHead>
            <TableHead className="text-right">Goals</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="text-muted-foreground py-10 text-center">
                No development plans yet.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((p) => (
              <TableRow key={p.id} className="cursor-pointer" onClick={() => onOpen(p)}>
                <TableCell className="font-medium">{p.title}</TableCell>
                <TableCell className="text-muted-foreground">{p.period || '—'}</TableCell>
                <TableCell>
                  <Badge variant={pdpStatusVariant(p.status)}>{pdpStatusLabel(p.status)}</Badge>
                </TableCell>
                <TableCell className="text-muted-foreground text-right text-xs">
                  {p.goals.length}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function PdpList({ rows, onOpen }: { rows: PDP[]; onOpen: (p: PDP) => void }) {
  if (rows.length === 0) return <EmptyText />;
  return (
    <ul className="divide-y rounded-md border">
      {rows.map((p) => (
        <li key={p.id}>
          <button
            type="button"
            onClick={() => onOpen(p)}
            className="hover:bg-accent/30 flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left text-sm"
          >
            <span className="min-w-0 flex-1 truncate font-semibold">{p.title}</span>
            <span className="text-muted-foreground hidden sm:inline">{p.period || ''}</span>
            <Badge variant={pdpStatusVariant(p.status)} className="shrink-0">
              {pdpStatusLabel(p.status)}
            </Badge>
          </button>
        </li>
      ))}
    </ul>
  );
}

function PdpCards({ rows, onOpen }: { rows: PDP[]; onOpen: (p: PDP) => void }) {
  if (rows.length === 0) return <EmptyText />;
  return (
    <div className="space-y-4">
      {rows.map((p) => (
        <Card
          key={p.id}
          role="button"
          tabIndex={0}
          className="hover:bg-accent/30 cursor-pointer transition-colors"
          onClick={() => onOpen(p)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpen(p);
            }
          }}
        >
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center justify-between gap-2 text-base">
              <span>{p.title}</span>
              <span className="flex items-center gap-2">
                {p.period ? <span className="text-muted-foreground text-sm">{p.period}</span> : null}
                <Badge variant={pdpStatusVariant(p.status)}>{pdpStatusLabel(p.status)}</Badge>
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {p.summary ? <p className="whitespace-pre-line">{p.summary}</p> : null}
            {p.goals.length > 0 ? (
              <div>
                <p className="text-xs font-medium uppercase">Goals</p>
                <ul className="mt-1 space-y-1">
                  {p.goals.map((g) => (
                    <li key={g.id} className="flex items-center gap-2">
                      <span className="text-foreground">{g.title}</span>
                      <Badge variant="outline">{goalStatusLabel(g.status)}</Badge>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function PdpGroupedByStatus({ rows, onOpen }: { rows: PDP[]; onOpen: (p: PDP) => void }) {
  const groups = useMemo(
    () =>
      PDP_STATUSES.map((s) => ({
        key: s.value,
        label: s.label,
        bucket: rows.filter((r) => r.status === s.value),
      })).filter((g) => g.bucket.length > 0),
    [rows],
  );
  if (rows.length === 0) return <EmptyText />;
  return (
    <div className="space-y-6">
      {groups.map(({ key, label, bucket }) => (
        <section key={key}>
          <h2 className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-semibold tracking-wider uppercase">
            {label}
            <Badge variant="outline" className="text-[10px]">
              {bucket.length}
            </Badge>
          </h2>
          <PdpCards rows={bucket} onOpen={onOpen} />
        </section>
      ))}
    </div>
  );
}

function EmptyText() {
  return <CabinetEmptyState>No development plans yet.</CabinetEmptyState>;
}
